import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import useModalEffect from '../../../hooks/useModalEffect';

const CreateTypeUnitForm = ({ isOpen, onClose, onSubmit, initialData }) => {
  useModalEffect(isOpen);
  const [formData, setFormData] = useState(
    initialData || {
      typeUnitName: '',
      description: ''
    }
  );
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        typeUnitName: initialData.typeUnitName || initialData.name || '',
        description: initialData.description || ''
      });
    } else {
      setFormData({ typeUnitName: '', description: '' });
    }
  }, [initialData, isOpen]);

  // Modal animation effects
  ;

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-backdrop');
      };
      
      cleanupModal();
    };
  }, []);


  // Cleanup effect: reset modal body state when component unmounts.
  useEffect(() => {
    return () => {
      // React owns the backdrop DOM (createPortal); only reset body state here.
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.typeUnitName.trim()) {
      errors.typeUnitName = 'Type Unit Name is required';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    return errors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
      setFormData({ typeUnitName: '', description: '' });
    } else {
      setFormErrors(errors);
    }
  };

  const handleModalClose = () => {
    setFormData({ typeUnitName: '', description: '' });
    setFormErrors({});
    onClose();
  };

  return (
    <>
      <style>{`
        #modal-create-type-unit .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-type-unit .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-type-unit .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-type-unit .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-type-unit .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-type-unit .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-create-type-unit"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initialData ? 'Edit Type Unit' : 'Create Type Unit'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Type Unit Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.typeUnitName ? 'is-invalid' : ''}`}
                    name="typeUnitName"
                    value={formData.typeUnitName}
                    onChange={handleInputChange}
                    placeholder="Enter type unit name"
                  />
                  {formErrors.typeUnitName && (
                    <div className="invalid-feedback">{formErrors.typeUnitName}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Description <span className="text-danger">*</span></label>
                  <textarea
                    className={`form-control ${formErrors.description ? 'is-invalid' : ''}`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    rows={4}
                  />
                  {formErrors.description && (
                    <div className="invalid-feedback">{formErrors.description}</div>
                  )}
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: '#9aa5b1' }}>
                    <span className="inv-required-star">*</span> Required fields
                  </span>
                </span>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-inv-save">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

CreateTypeUnitForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default CreateTypeUnitForm; 